import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store/store";
import {
  setPaymentData,
  // setPaymentSummary, // Commented out since not using summary
  setLoading,
  setError,
  setFetchFilter,
  setFilter,
  setSearchQuery,
  setPagination,
  setSelectedTransaction,
  setSortBy,
  paymentSelector,
} from "@/store/features/paymentSlice";
import { usePayments, usePaymentStats } from "@/services/payment.services";

export const usePaymentHistory = (disableApiCalls: boolean = false) => {
  const dispatch = useDispatch<AppDispatch>();
  const paymentState = useSelector(paymentSelector);
  const { pagination, searchQuery, fetchFilters, sortBy } = paymentState;

  // Prepare API parameters with memoization to prevent unnecessary refetches
  const apiParams = useMemo(() => {
    // Default date range (30 days ago to tomorrow)
    const currentDate = new Date();
    const defaultFromDate = new Date();
    defaultFromDate.setDate(currentDate.getDate() - 30);
    
    const defaultToDate = new Date();
    defaultToDate.setDate(currentDate.getDate() + 1);

    const baseParams = {
      page: pagination.pageNumber,
      per_page: pagination.pageSize,
      search: searchQuery,
      filters: fetchFilters,
      sortBy,
      from_date: defaultFromDate.toISOString().split('T')[0],
      to_date: defaultToDate.toISOString().split('T')[0],
    };

    // Check if there are any filters applied
    if (fetchFilters && fetchFilters.length > 0) {
      const receivedOnFilter = fetchFilters.find((filter: any) => 
        filter.field?.value === 'received_on'
      );
      
      // If there's a date filter, use the date filter values
      if (receivedOnFilter) {
        const operator = typeof receivedOnFilter.operator === 'object' && receivedOnFilter.operator !== null
          ? (receivedOnFilter.operator as any)?.value 
          : receivedOnFilter.operator;
        const values = receivedOnFilter.value || [];
        
        if (operator === 'between' && values.length >= 2) {
          // Use the date range from the filter
          return {
            ...baseParams,
            from_date: values[0],
            to_date: values[1],
          };
        } else if (operator === 'on' && values.length >= 1) {
          // For "on" operator, use the same date for both from and to
          return {
            ...baseParams,
            from_date: values[0],
            to_date: values[0],
          };
        } else if (operator === 'before' && values.length >= 1) {
          // For "before" operator, use a very early date to the selected date
          return {
            ...baseParams,
            from_date: '1900-01-01',
            to_date: values[0],
          };
        } else if (operator === 'after' && values.length >= 1) {
          // For "after" operator, use the selected date to a far future date
          return {
            ...baseParams,
            from_date: values[0],
            to_date: '2100-12-31',
          };
        }
      }
      
      // If there are non-date filters, remove the date parameters
      const hasNonDateFilters = fetchFilters.some((filter: any) => 
        filter.field?.value !== 'received_on'
      );
      
      if (hasNonDateFilters) {
        const { from_date, to_date, ...paramsWithoutDates } = baseParams;
        return paramsWithoutDates;
      }
    }

    // Return base params with default date parameters when no filters are applied
    return baseParams;
  }, [pagination.pageNumber, pagination.pageSize, searchQuery, fetchFilters, sortBy]);

  console.log("apiParams",  !disableApiCalls);
  // Fetch payments data
  const {
    data: paymentsResponse,
    isLoading: paymentsLoading,
    error: paymentsError,
    refetch: refetchPayments,
  } = usePayments({
    ...apiParams,
    enabled: !disableApiCalls,
  });

  // Fetch payment stats data
  const {
    refetch: refetchPaymentStats,
  } = usePaymentStats();

  // Update Redux state when data changes
  useEffect(() => {
    if (paymentsResponse) {
      // Handle different response formats from backend
      const paymentData = paymentsResponse.data || paymentsResponse || [];
      const processedData = Array.isArray(paymentData) ? paymentData : [];
      dispatch(setPaymentData(processedData));
    }
  }, [paymentsResponse, dispatch]);

  // Auto-refresh data when sortBy changes
  useEffect(() => {
    if (sortBy && sortBy.length > 0) {
      refetchPayments();
    }
  }, [sortBy, refetchPayments]);

  // Auto-refresh data when pagination changes
  useEffect(() => {
    refetchPayments();
  }, [pagination.pageNumber, pagination.pageSize, refetchPayments]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isLoading = paymentsLoading;
    dispatch(setLoading(isLoading));
  }, [paymentsLoading, dispatch]);

  useEffect(() => {
    const error = paymentsError;
    dispatch(setError(error?.message || null));
  }, [paymentsError, dispatch]);

  // Helper functions
  const refreshData = async () => {
    try {
      // Run both refetch operations in parallel for faster loading
      await Promise.all([
        refetchPayments(),
        refetchPaymentStats()
      ]);
    } catch (error) {
      console.error('Error refreshing payment data:', error);
    }
  };

  const applyFilters = async (newFilters?: any[]) => {
    dispatch(setFetchFilter(newFilters));
    dispatch(setFilter(newFilters || []));
    await refreshData();
  };  

  return {
    ...paymentState,
    refreshData,
    applyFilters,
    totalPages: paymentsResponse?.totalPages || 0,
    totalCount: paymentsResponse?.pagination?.total || 0,
    setSearchQuery: (query: string) => dispatch(setSearchQuery(query)),
    setPagination: (pagination: any) => dispatch(setPagination(pagination)),
    setSelectedTransaction: (transaction: any) => dispatch(setSelectedTransaction(transaction)),
    setSortBy: (sortBy: any) => dispatch(setSortBy(sortBy)),
  };
};
